import { c as _c } from "react/compiler-runtime";
import { APIProvider, ControlPosition, Map, MapCameraChangedEvent, MapControl } from '@vis.gl/react-google-maps';
import debounce from 'lodash.debounce';
import React from 'react';
import axios from 'shared/anaxios';
import { Box } from 'shared/grid';
import useComponentSize from 'shared/hooks/component-size';
import { MapAutocomplete } from './autocomplete';
const PIN_PATH = `M20.2,15.7L20.2,15.7c1.1-1.6,1.8-3.6,1.8-5.7c0-5.6-4.5-10-10-10S2,4.5,2,10c0,2,0.6,3.9,1.6,5.4c0,0.1,0.1,0.2,0.2,0.3
  c0,0,0.1,0.1,0.1,0.2c0.2,0.3,0.4,0.6,0.7,0.9c2.6,3.1,7.4,7.6,7.4,7.6s4.8-4.5,7.4-7.5c0.2-0.3,0.5-0.6,0.7-0.9
  C20.1,15.8,20.2,15.8,20.2,15.7z`;
const PinMarker = t0 => {
  const $ = _c(7);
  const {
    size: t1,
    color
  } = t0;
  const size = t1 === undefined ? 20 : t1;
  const t2 = `translate(${-size / 2}px,${-size}px)`;
  let t3;
  if ($[0] !== color || $[1] !== t2) {
    t3 = {
      pointerEvents: "none",
      cursor: "pointer",
      stroke: "#fff",
      strokeWidth: 2,
      fill: color,
      transform: t2
    };
    $[0] = color;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <path d={PIN_PATH} />;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== size || $[5] !== t3) {
    t5 = <svg height={size} viewBox="0 0 24 24" style={t3}>{t4}</svg>;
    $[4] = size;
    $[5] = t3;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  return t5;
};
const geocodeToFormItems = item => {
  const location = item.address_components.reduce((agg, val) => {
    if (val.types.includes('street_number')) {
      agg['street_number'] = val.long_name;
    }
    if (val.types.includes('route')) {
      agg['street_name'] = val.long_name;
    }
    if (val.types.includes('sublocality_level_2')) {
      agg['suburb'] = val.long_name;
    }
    if (val.types.includes('locality')) {
      agg['city'] = val.long_name;
    }
    if (val.types.includes('administrative_area_level_1')) {
      agg['province'] = val.long_name;
    }
    if (val.types.includes('country')) {
      agg['country'] = val.short_name;
    }
    if (val.types.includes('postal_code')) {
      agg['postal_code'] = val.short_name;
    }
    return agg;
  }, {
    street_number: '',
    street_name: '',
    suburb: '',
    city: '',
    province: '',
    country: '',
    postal_code: ''
    // gps: [item.geometry.location.lat, item.geometry.location.lng],
  });
  const {
    street_number,
    street_name,
    suburb,
    ...rest
  } = location;
  return {
    street_address: [[street_number, street_name].filter(v => v).join(' '), suburb].filter(v => v).join('\n'),
    ...rest
  };
};
const placeToFormItems = place => {
  const location = place.addressComponents.reduce((agg, val) => {
    if (val.types.includes('street_number')) {
      agg['street_number'] = val.longText;
    }
    if (val.types.includes('route')) {
      agg['street_name'] = val.longText;
    }
    if (val.types.includes('sublocality_level_2')) {
      agg['suburb'] = val.longText;
    }
    if (val.types.includes('locality')) {
      agg['city'] = val.longText;
    }
    if (val.types.includes('administrative_area_level_1')) {
      agg['province'] = val.longText;
    }
    if (val.types.includes('country')) {
      agg['country'] = val.shortText;
    }
    if (val.types.includes('postal_code')) {
      agg['postal_code'] = val.shortText;
    }
    return agg;
  }, {
    street_number: '',
    street_name: '',
    suburb: '',
    city: '',
    province: '',
    country: '',
    postal_code: '',
    gps: [place.location.lat(), place.location.lng()]
  });
  const {
    street_number,
    street_name,
    suburb,
    ...rest
  } = location;
  return {
    street_address: [[street_number, street_name].filter(v => v).join(' '), suburb].filter(v => v).join('\n'),
    ...rest
  };
};
const geocode = debounce(({
  lat,
  lng
}, callback) => {
  return axios.get(`https://maps.googleapis.com/maps/api/geocode/json`, {
    params: {
      // proximity: `${state.geoip.longitude},${state.geoip.latitude}`,
      latlng: `${lat},${lng}`,
      key: __GOOGLE_MAPS_KEY__
    }
  }).then(({
    data
  }) => {
    const results = data.results.map(r => ({
      ...r,
      text: r.formatted_address
    }));
    if (results.length) {
      const parts = geocodeToFormItems(results[0]);
      callback(parts);
    } else {
      callback({});
    }
  });
}, 1000);
export const LocationMap = t0 => {
  const $ = _c(32);
  const {
    defaultCenter: t1,
    center,
    setCenter,
    onGeoCode
  } = t0;
  let t2;
  if ($[0] !== t1) {
    t2 = t1 === undefined ? [0, 0] : t1;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const defaultCenter = t2;
  let t3;
  bb0: {
    if (center && center.length === 2) {
      let t4;
      if ($[2] !== center[0] || $[3] !== center[1]) {
        t4 = {
          lat: center[0],
          lng: center[1]
        };
        $[2] = center[0];
        $[3] = center[1];
        $[4] = t4;
      } else {
        t4 = $[4];
      }
      t3 = t4;
      break bb0;
    }
    let t4;
    if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = {
        lat: 0,
        lng: 0
      };
      $[5] = t4;
    } else {
      t4 = $[5];
    }
    t3 = t4;
  }
  const initialCenter = t3;
  let t4;
  if ($[6] !== center[0] || $[7] !== center[1] || $[8] !== initialCenter.lat || $[9] !== initialCenter.lng || $[10] !== onGeoCode || $[11] !== setCenter) {
    t4 = e => {
      const {
        lat,
        lng
      } = e.detail.center;
      setCenter([lat, lng]);
      if (lat && lng && Math.abs(center[0] - lat) > 0.0001 && Math.abs(center[1] - lng) > 0.0001 && Math.abs(initialCenter.lat - lat) > 0.0001 && Math.abs(initialCenter.lng - lng) > 0.0001) {
        geocode({
          lat,
          lng
        }, t5 => {
          const {
            gps,
            ...place
          } = t5;
          return onGeoCode(place);
        });
      }
    };
    $[6] = center[0];
    $[7] = center[1];
    $[8] = initialCenter.lat;
    $[9] = initialCenter.lng;
    $[10] = onGeoCode;
    $[11] = setCenter;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const onCameraChanged = t4;
  let t5;
  if ($[13] !== onGeoCode) {
    t5 = place_0 => {
      const parts = placeToFormItems(place_0);
      onGeoCode(parts);
    };
    $[13] = onGeoCode;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const onPlacesChanged = t5;
  const mapWrapperRef = React.useRef(null);
  const {
    width: mapWidth
  } = useComponentSize(mapWrapperRef);
  let t6;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      width: "100%",
      position: "relative",
      zIndex: 1
    };
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <Box sx={{
      position: "absolute",
      top: "50%",
      left: "50%",
      zIndex: 1
    }}><PinMarker color="#dc4a3d" size={30} /></Box>;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      width: "100%",
      height: "600px"
    };
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  const t9 = center[0] || defaultCenter[0];
  const t10 = center[1] || defaultCenter[1];
  let t11;
  if ($[18] !== t10 || $[19] !== t9) {
    t11 = {
      lat: t9,
      lng: t10
    };
    $[18] = t10;
    $[19] = t9;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== mapWidth) {
    t12 = {
      zIndex: 1,
      width: mapWidth
    };
    $[21] = mapWidth;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== onPlacesChanged) {
    t13 = <MapAutocomplete onPlaceSelect={onPlacesChanged} />;
    $[23] = onPlacesChanged;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] !== t12 || $[26] !== t13) {
    t14 = <MapControl position={ControlPosition.LEFT_TOP}><Box p={4} sx={t12}>{t13}</Box></MapControl>;
    $[25] = t12;
    $[26] = t13;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] !== onCameraChanged || $[29] !== t11 || $[30] !== t14) {
    t15 = <Box ref={mapWrapperRef} sx={t6}>{t7}<APIProvider apiKey={__GOOGLE_MAPS_KEY__}><Map style={t8} center={t11} defaultZoom={13} zoomControl={true} gestureHandling="cooperative" disableDefaultUI={true} onCameraChanged={onCameraChanged}>{t14}</Map></APIProvider></Box>;
    $[28] = onCameraChanged;
    $[29] = t11;
    $[30] = t14;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  return t15;
};